import React from 'react';
import { Dialog, DialogContent, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, ChevronLeft, ChevronRight, MessageCircle, UserPlus, UserCheck, MapPin, Briefcase } from 'lucide-react';
import { Member } from '@/types/member';
import { TeamMemberBadge } from '@/components/ui/team-member-badge';

interface PhotoZoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: Member;
  allMembers?: Member[];
  currentIndex?: number;
  onNavigate?: (direction: 'prev' | 'next') => void;
  onMessage?: (member: Member) => void;
  onConnect?: (member: Member) => void;
  connectionStatus?: string | null;
}

export const PhotoZoomModal: React.FC<PhotoZoomModalProps> = ({
  isOpen,
  onClose,
  member,
  allMembers,
  currentIndex,
  onNavigate,
  onMessage,
  onConnect,
  connectionStatus
}) => {
  const canNavigate = allMembers && allMembers.length > 1 && currentIndex !== undefined;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!canNavigate || !onNavigate) return;
    
    if (e.key === 'ArrowLeft') {
      onNavigate('prev');
    } else if (e.key === 'ArrowRight') {
      onNavigate('next');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-4xl w-[95vw] h-[90vh] p-0 overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        <DialogClose className="absolute right-4 top-4 z-10 rounded-full bg-black/50 text-white hover:bg-black/70 h-10 w-10 flex items-center justify-center">
          <X className="h-5 w-5" />
          <span className="sr-only">Fermer</span>
        </DialogClose>

        {/* Navigation Arrows */}
        {canNavigate && onNavigate && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 rounded-full bg-black/50 text-white hover:bg-black/70 h-12 w-12"
              onClick={() => onNavigate('prev')}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-16 top-1/2 -translate-y-1/2 z-10 rounded-full bg-black/50 text-white hover:bg-black/70 h-12 w-12"
              onClick={() => onNavigate('next')}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </>
        )}

        <div className="flex h-full">
          {/* Photo Section */}
          <div className="flex-1 bg-black flex items-center justify-center">
            <img
              src={member.image}
              alt={member.name}
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: '90vh' }}
            />
          </div>

          {/* Info Panel */}
          <div className="w-80 bg-background p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Header */}
              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-foreground">{member.name}</h2>
                {member.is_team_member && (
                  <div className="flex">
                    <TeamMemberBadge size="sm" />
                  </div>
                )}
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Briefcase className="h-4 w-4" />
                    <span className="text-sm">{member.role}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{member.company}</p>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm">{member.city}, {member.country}</span>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {member.bio && (
                <div>
                  <h3 className="font-semibold text-foreground mb-2">À propos</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{member.bio}</p>
                </div>
              )}

              {/* Skills */}
              {member.skills && member.skills.length > 0 && (
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Compétences</h3>
                  <div className="flex flex-wrap gap-1">
                    {member.skills.map((skill, index) => (
                      <Badge key={index} variant="green" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Languages */}
              {member.languages && member.languages.length > 0 && (
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Langues</h3>
                  <div className="flex flex-wrap gap-1">
                    {member.languages.map((language, index) => (
                      <Badge key={index} variant="blue" className="text-xs">
                        {language}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Needs */}
              {member.needs && member.needs.length > 0 && (
                <div>
                  <h3 className="font-semibold text-foreground mb-2">Recherche</h3>
                  <div className="flex flex-wrap gap-1">
                    {member.needs.map((need, index) => (
                      <Badge key={index} variant="purple" className="text-xs">
                        {need}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3 pt-4 border-t">
                {onMessage && (
                  <Button
                    onClick={() => onMessage(member)}
                    className="w-full"
                    size="sm"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Envoyer un message
                  </Button>
                )}
                
                {onConnect && connectionStatus !== 'accepted' && (
                  <Button
                    onClick={() => onConnect(member)}
                    variant="outline"
                    className="w-full"
                    size="sm"
                    disabled={connectionStatus === 'pending'}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {connectionStatus === 'pending' ? 'En attente...' : 'Demander une mise en relation'}
                  </Button>
                )}
                
                {connectionStatus === 'accepted' && (
                  <div className="flex items-center justify-center gap-2 text-sm text-green-600 bg-green-50 py-2 px-4 rounded-md">
                    <UserCheck className="h-4 w-4" />
                    <span className="font-medium">Vous êtes connectés</span>
                  </div>
                )}
              </div>

              {/* Navigation indicator */}
              {canNavigate && (
                <div className="text-center text-xs text-muted-foreground pt-2 border-t">
                  {(currentIndex || 0) + 1} sur {allMembers?.length}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};